/**
 * @module Components.CalendarColorPicker
 *
 * Swatch picker for calendar colour coding: the colour a booking option's
 * sessions are drawn in on the calendar.
 *
 * A radiogroup rather than eight checkboxes: exactly one colour applies, and
 * "None" is a real choice (it means "fall back to the default"), not an absent
 * selection. Each swatch carries its colour name, because a control that only
 * distinguishes options by hue is unusable for anyone who cannot separate them.
 */
import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { cls } from "app/utils";
import { uuid } from "app/utils/uuid";
import { CALENDAR_COLORS, isCalendarColor } from "app/utils/calendarColors";

import styles from "./styles.module.scss";

type PropsDef = {
  value?: string | null;
  onChange: (color: string | null) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
  testId?: string;
};

export default function CalendarColorPicker({
  value,
  onChange,
  label,
  hint,
  disabled = false,
  testId = "calendar-color-picker",
}: PropsDef) {
  const { t } = useTranslation();
  // A colour outside the palette (legacy data, or a casing the backend once
  // accepted) selects nothing, and selection is what makes a swatch tabbable --
  // so an unrecognized value would leave the whole radiogroup unreachable by
  // keyboard. Treat it as untagged: "None" is then selected and focusable, and
  // the coach can pick a real colour.
  const selected = isCalendarColor(value) ? (value as string) : null;
  // Focus has to move with the selection (below), so the swatches need refs.
  const swatchRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const labelId = useMemo(() => `calendar-color-label-${uuid()}`, []);

  const options: Array<{ value: string | null, name: string }> = [
    { value: null, name: t("None") },
    ...CALENDAR_COLORS.map((color) => ({
      value: color.value,
      name: t(color.name),
    })),
  ];

  // Arrow keys move between swatches, which is what a radiogroup is expected to
  // do — the swatches are buttons, so it has to be wired by hand.
  const onKeyDown = (event: React.KeyboardEvent, index: number) => {
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;

    if (step === 0) {
      return;
    }

    event.preventDefault();
    const next = (index + step + options.length) % options.length;
    onChange(options[next].value);
    // Selection drives tabIndex, so without moving focus the arrow keys would
    // leave focus on a swatch that is no longer reachable by Tab — and a
    // screen reader would never announce the new choice.
    swatchRefs.current[next]?.focus();
  };

  return (
    <div className={styles.container} data-testid={testId}>
      {/* Not a <label>: it labels a radiogroup, not a single form control, so
          it is associated with aria-labelledby instead of htmlFor. */}
      {label && (
        <span className={styles.label} id={labelId}>
          {label}
        </span>
      )}
      <div
        className={styles.swatches}
        role="radiogroup"
        aria-labelledby={label ? labelId : undefined}
        aria-label={label ? undefined : t("Calendar colour")}
      >
        {options.map((option, index) => {
          const isSelected = selected === option.value;

          return (
            <button
              key={option.value || "none"}
              ref={(el) => {
                swatchRefs.current[index] = el;
              }}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={option.name}
              title={option.name}
              disabled={disabled}
              tabIndex={isSelected ? 0 : -1}
              className={cls(styles.swatch, {
                [styles.selected]: isSelected,
                [styles.none]: option.value === null,
              })}
              // The selected ring is the swatch's own colour with a white gap
              // inside it, so the choice reads as that colour rather than as a
              // dark outline bolted onto it.
              style={
                option.value
                  ? {
                      backgroundColor: option.value,
                      ...(isSelected
                        ? {
                            boxShadow: `0 0 0 2px #fff inset, 0 0 0 2px ${option.value}`,
                          }
                        : {}),
                    }
                  : undefined
              }
              onClick={() => onChange(option.value)}
              onKeyDown={(event) => onKeyDown(event, index)}
              data-testid={`${testId}-${option.value ? option.value.replace("#", "") : "none"}`}
            >
              {option.value === null && (
                <span className={styles.none_mark} aria-hidden="true" />
              )}
              {isSelected && option.value !== null && (
                <i className="fa fa-check" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
      {hint && <span className={styles.hint}>{hint}</span>}
    </div>
  );
}
